import { CancelTokenSource } from 'axios';
import axios from 'services/http';
import {
  TreatmentBookingDataDto,
  TreatmentDto,
  TreatmentOverviewSearchRequest,
  TreatmentOverviewSearchResponse,
  TreatmentPatientGuarantorDto,
  TreatmentPatientGuarantorRequest,
  TreatmentTariffDto,
  UpsertTreatmentDto
} from 'ts-types/api.types';

const {REACT_APP_API_BASE_URL: baseUrl} = process.env;
const apiUri = `${baseUrl}/api/treatments`;

export const getAllTreatmentsByPatientId = (
    source: CancelTokenSource, patientId?: Number): Promise<Array<TreatmentDto>> => {

  return axios
      .get(`${apiUri}/by-patient/${patientId}`, {cancelToken: source.token})
      .then((response) => response.data);
};

export const getTreatmentsByPatientIdAndDeleted = (
    patientId: number | undefined,
    includeDeleted: boolean,
    source: CancelTokenSource): Promise<Array<TreatmentDto>> => {

  return axios
      .get(`${apiUri}/by-patient-and-deleted/${patientId}`, {params: {includeDeleted}, cancelToken: source.token})
      .then((response) => response.data);
};

export const getTreatmentBookingData = (
    treatmentId: number, source: CancelTokenSource): Promise<TreatmentBookingDataDto> => {

  return axios
      .get(`${apiUri}/booking-data`, {params: {treatmentId}, cancelToken: source.token})
      .then((response) => response.data);
};

export const getTreatmentData = (
    treatmentId: number,
    source: CancelTokenSource): Promise<TreatmentDto> => {

  return axios
      .get(`${apiUri}/${treatmentId}/extended`, {cancelToken: source.token})
      .then((response) => response.data);
};

export const getTreatmentById = (
    treatmentId: number, source: CancelTokenSource): Promise<UpsertTreatmentDto> => {

  return axios
      .get(`${apiUri}/${treatmentId}`, {cancelToken: source.token})
      .then((response) => response.data);
};

export const insertTreatment = (
    newTreatment: Partial<UpsertTreatmentDto>, source: CancelTokenSource): Promise<TreatmentDto> => {

  return axios
      .post(apiUri, newTreatment, {cancelToken: source.token})
      .then((response) => response.data);
};

export const editTreatment = (treatmentId: number, editTreatment: Partial<UpsertTreatmentDto>,
                              source: CancelTokenSource): Promise<TreatmentDto> => {
  return axios
      .put(`${apiUri}/${treatmentId}`, editTreatment, {cancelToken: source.token})
      .then((response) => response.data);
};

export const deleteTreatment = (
    treatmentId: number, source: CancelTokenSource): Promise<String> => {

  return axios
      .delete(`${apiUri}/${treatmentId}`, {cancelToken: source.token})
      .then((response) => response.data)
};

export const undeleteTreatment = (
    treatmentId: number, patientId: number, source: CancelTokenSource): Promise<TreatmentDto[]> => {

  return axios
      .put(`${apiUri}/undelete/${treatmentId}`, {}, {params: {patientId: patientId}, cancelToken: source.token})
      .then((response) => response.data)
};

export const closeTreatment = (
    treatmentId: number, source: CancelTokenSource): Promise<TreatmentDto> => {

  return axios
      .get(`${apiUri}/close/${treatmentId}`, {cancelToken: source.token})
      .then(response => response.data);
};


export const getPatientGuarantorForTariff = (
    treatmentPatientGuarantorRequest: TreatmentPatientGuarantorRequest,
    source: CancelTokenSource): Promise<TreatmentPatientGuarantorDto> => {

  return axios
      .post(`${apiUri}/patient-guarantor`, treatmentPatientGuarantorRequest, {cancelToken: source.token})
      .then(response => response.data);
};

export const getTreatmentTariffs = (
    treatmentId: number, source: CancelTokenSource): Promise<Array<TreatmentTariffDto>> => {

  return axios
      .get(`${apiUri}/treatment-tariffs/${treatmentId}`, {cancelToken: source.token})
      .then(response => response.data);
};

export const getTreatmentOverview = (
    searchRequest: Partial<TreatmentOverviewSearchRequest>,
    source: CancelTokenSource): Promise<TreatmentOverviewSearchResponse> => {

  return axios
      .post(`${apiUri}/overview`, searchRequest, {cancelToken: source.token})
      .then(response => response.data);
};

export const exportTreatmentOverview = (
    searchRequest: Partial<TreatmentOverviewSearchRequest>,
    source: CancelTokenSource) => {

  return axios
      .post(`${apiUri}/overview-export`, searchRequest,
          {responseType: 'blob', cancelToken: source.token})
      .then(response => {
        const type = response.headers['content-type'];
        const blob = new Blob([response.data], {type: type});
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = response.headers['content-disposition'];
        const filenameRegex = /filename=(.*)/;
        const regexResponse = filenameRegex.exec(link.download);
        const defaultFileName = "response.csv";
        const fileName = regexResponse ?
            regexResponse[1] ? regexResponse[1] : defaultFileName :
            defaultFileName;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
      })
};
